import { useState } from "react";
import { Phone, MapPin, Home, Clock, AlertTriangle, RotateCcw, Flame, Sparkles, FileText, Calendar, CheckCircle, Wallet, Users, CalendarDays } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Analytics } from "@/lib/analytics";
import { signupUrl } from "@/lib/productApp";
import CalendlyModal from "@/components/CalendlyModal";
import { AnimatePresence, motion } from "framer-motion";
import type { AdsMode } from "./AdsModeSelector";

const venteFields = [
  { icon: MapPin, label: "Projet", value: "Achat" },
  { icon: Home, label: "Type", value: "T3 — Lyon 6", badge: "Exclu possible" },
  { icon: AlertTriangle, label: "Urgence", value: "Forte", highlight: true, showProgress: true },
  { icon: Clock, label: "Appelé à", value: "18h43" },
  { icon: RotateCcw, label: "Statut", value: "En attente de rappel" },
];

const locationFields = [
  { icon: MapPin, label: "Projet", value: "Location" },
  { icon: Home, label: "Type", value: "Studio — Bordeaux Centre" },
  { icon: Wallet, label: "Revenu", value: "2 400 €/mois" },
  { icon: Users, label: "Garant", value: "Oui (parents)" },
  { icon: CalendarDays, label: "Disponibilité", value: "Immédiate" },
  { icon: RotateCcw, label: "Statut", value: "En attente de rappel" },
];

const venteCard = {
  name: "M. Dupont",
  badge: "Client chaud",
  badgeVariant: "destructive" as const,
  BadgeIcon: Flame,
  time: "Aujourd'hui à 18h43 · Reçu par Immovox",
  fields: venteFields,
  summary: "Souhaite visiter ce weekend. Budget 350k€. Première acquisition, prêt pré-accordé.",
  bottomText: "Chaque appel génère une fiche comme celle-ci. Budget, projet, urgence : tout est là.",
  ctaText: "Recevoir mes fiches",
};

const locationCard = {
  name: "Mme Martin",
  badge: "Dossier complet",
  badgeVariant: "default" as const,
  BadgeIcon: CheckCircle,
  time: "Aujourd'hui à 14h12 · Reçu par Immovox",
  fields: locationFields,
  summary: "Salarié dans le privé, CDI. Revenus 2 400 €/mois + garants parents. Souhaite emménager avant le 1er.",
  bottomText: "Chaque appel génère une fiche comme celle-ci. Solvabilité, garant, disponibilité : tout est là.",
  ctaText: "Recevoir mes fiches",
};

const AdsDashboardMockup = ({ mode }: { mode: AdsMode }) => {
  const containerRef = useScrollReveal({});
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const card = mode === "vente" ? venteCard : locationCard;

  return (
    <>
      <section className="section-padding bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground">
              {mode === "vente"
                ? "Ce prospect a appelé à 18h43. Vous étiez en visite."
                : "Ce candidat a appelé à 14h12."}
            </h2>
            <p className="text-lg text-muted-foreground mt-3">
              Voici la fiche que vous auriez reçue.
            </p>
          </div>

          <div ref={containerRef} className="max-w-lg mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
              >
                {/* Mockup card */}
                <div className="card-stripe p-0 overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center gap-3 px-6 py-4 bg-primary/5 border-b border-border/50">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{card.name}</p>
                        <Badge variant={card.badgeVariant} className="text-[10px] px-1.5 py-0">
                          <card.BadgeIcon className="w-3 h-3 mr-0.5" />
                          {card.badge}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{card.time}</p>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="p-6 space-y-4">
                    {card.fields.map((field, i) => (
                      <div key={i}>
                        <div className="flex items-center gap-3">
                          <field.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="text-sm text-muted-foreground w-20 shrink-0">{field.label}</span>
                          <span className={`text-sm font-medium ${field.highlight ? "text-destructive" : "text-foreground"}`}>
                            {field.value}
                          </span>
                          {field.badge && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary border-0">
                              {field.badge}
                            </Badge>
                          )}
                        </div>
                        {field.showProgress && (
                          <div className="ml-[7.5rem] mt-1.5">
                            <Progress value={85} className="h-1.5 bg-destructive/10 [&>div]:bg-destructive" />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* AI Summary */}
                    <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles className="w-3.5 h-3.5 text-primary" />
                        <span className="text-xs font-semibold text-primary">Résumé IA</span>
                      </div>
                      <p className="text-sm text-foreground">{card.summary}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Benefits */}
            <div className="mt-8 space-y-3 max-w-md mx-auto">
              {[
                "Envoyée par e-mail dès la fin de l'appel",
                "Consultable sur mobile, entre deux visites",
                "Plus besoin de réécouter un message vocal",
              ].map((benefit, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <p className="text-sm text-muted-foreground">{benefit}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-foreground mt-6 font-semibold text-lg">
              {card.bottomText}
            </p>

            {/* CTA */}
            <div className="flex flex-col items-center gap-3 mt-8">
              <Button
                size="lg"
                className="h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-warm"
                asChild
              >
                <a
                  href={signupUrl()}
                  onClick={() => Analytics.ctaClicked("activer_filet", "ads_dashboard_mockup")}
                >
                  <FileText className="w-5 h-5 mr-2" />
                  {card.ctaText}
                </a>
              </Button>
              <button
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  Analytics.ctaClicked("planifier_demo", "ads_dashboard_mockup");
                  setCalendlyOpen(true);
                }}
              >
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                Ou réserver une démo
              </button>
            </div>
          </div>
        </div>
      </section>
      <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} source="ads_dashboard_mockup" />
    </>
  );
};

export default AdsDashboardMockup;
